import { LoadingService } from './../../services/loading.service';
import { AfterViewInit, booleanAttribute, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MediaDisplayerService } from "../../services/media-displayer.service";
import { SnackbarService } from "../../services/snackbar.service";
import { AttachmentExtensionEnum } from "../../../models/enums/attachment-extension-enum";
import { AttachmentUtils } from 'src/app/core/utilities/attachment-utils';
import { AttachmentService } from '@shared/services/attachment.service';

@Component({
  selector: 'sigecom-file-upload',
  template: `
    <sigecom-input-container [label]="label" [isRequired]="isRequired"
                             [errorMessage]="errorMessage" [enableRequiredBorder]="enableRequiredBorder" 
                             [class]="statusBorderClass" >
      <div class="flex flex-col justify-between">
        <div *ngIf="!isLoading" class="flex justify-between">
          <label #contentWrapper class="p-2 w-full flex items-center gap-2 text-sigecom-dark-gray cursor-pointer">
            <fa-icon [icon]="'file-arrow-up'"></fa-icon>

            <input
            class="w-full"
            sigecom-input type="file" [accept]="accepts"
            (change)="getAttachment($event)"
            [required]="isRequired"
            [disabled]="isDisabled"
            />

            {{ lastBase64Message }}
            <ng-content></ng-content>
          </label>
          <fa-icon [icon]="'eye'" class="text-sigecom-dark-gray" (click)="showImage()"></fa-icon>
        </div>

        <!-- Barra de progresso com texto -->
        <div class="px-2" *ngIf="isLoading">
          <sigecom-progress-bar [progress]="progress"></sigecom-progress-bar>
        </div>

      </div>
    </sigecom-input-container>
  `,
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements AfterViewInit {
  @ViewChild('contentWrapper') content?: ElementRef;

  @Output() base64: EventEmitter<string> = new EventEmitter<string>();
  @Output() format: EventEmitter<AttachmentExtensionEnum> = new EventEmitter<AttachmentExtensionEnum>();

  @Input({ transform: booleanAttribute }) enableRequiredBorder: boolean = false;
  @Input() aneNrId?: number | null;
  @Input() lastBase64?: string;
  @Input() label: string = "[ DEFAULT LABEL ]";
  @Input() statusBorderClass = '';

  @Input() isRequired: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() errorMessage?: string;

  @Input() accepts?: string = "image/png, image/jpeg, application/pdf";

  public readonly defaultMessage: string = "Selecione o arquivo...";
  @Input() acceptedTypes: string[] = ["IMAGE/PNG", "IMAGE/JPEG", "IMAGE/JPG", "APPLICATION/PDF"];

  isLoading = false;
  progress = 0;
  private progressInterval: any;
  private readonly MAX_FILE_SIZE_MB = 50;
  FILE_SIZE_MB!: number;

  constructor(
    private mediaDisplayerService: MediaDisplayerService,
    private snackbarService: SnackbarService,
    private attachmentService: AttachmentService,
    private loadingService: LoadingService,
    private cd: ChangeDetectorRef
  ) {
  }

  ngAfterViewInit(): void {
    const inputs: HTMLInputElement[] = this.content?.nativeElement.getElementsByTagName("input");
    for (let i = 0; i < inputs.length; i++) {
      if (i != 0) {
        inputs[i].type = "hidden";
      }
    }
  }

  public showImage() {
    if (!this.lastBase64) return;

    if (AttachmentUtils.isBase64(this.lastBase64)) {
      this.mediaDisplayerService.sendContent(this.lastBase64);
      return;
    }

    if (this.aneNrId) {
      this.attachmentService.findAttachmentById$(this.aneNrId).subscribe({
        next: (base64) => this.mediaDisplayerService.sendContent(base64),
        error: (err) => this.snackbarService.showDanger(err),
      });
      return;
    }

    // fallback: mantém comportamento antigo pra quem ainda não passa aneNrId
    this.mediaDisplayerService.sendContent(this.lastBase64);
}

  public getAttachment($event: Event) {
    const target = $event.target as HTMLInputElement;
    const file: File = target.files?.item(0) as File;

    this.FILE_SIZE_MB = file.size / (1024 * 1024);

    if (!file || !this.isValidFileType(file) || !this.isValidFileSize(file)) return;

    this.handleFileFormat(file);
    this.handleProgress();

    this.attachmentService.getBase64OrCompressed(file).subscribe({
      next: (res) => this.handleResponse(res),
      error: (err) => this.handleError(err)
    });
  }

  private isValidFileType(file: File): boolean {
    const fileType = file.type.toUpperCase();
    const isValid = this.acceptedTypes.some(acceptedType => acceptedType === fileType);
    if (!isValid) {
      this.snackbarService.showWarn("Este formato não é permitido.");
    }
    return isValid;
  }

  private isValidFileSize(file: File): boolean {
    if (this.FILE_SIZE_MB > this.MAX_FILE_SIZE_MB) {
      this.snackbarService.showWarn("O arquivo ultrapassa o limite de 50MB. Envie um menor.");
      return false;
    }

    return true;
  }

  private handleFileFormat(file: File): void {
    const attachmentExtensionEnumKey: string = file.type.split("/")[1].toUpperCase();
    const format = AttachmentExtensionEnum[attachmentExtensionEnumKey as keyof typeof AttachmentExtensionEnum];
    this.format.emit(format);
  }

  private handleProgress(): void {
    if (this.FILE_SIZE_MB > 2) {
      this.isLoading = true;
      this.isDisabled = true;
      this.startProgressBar();
    }
  }

  private handleResponse(res: string): void {
    this.progress = 100;
    this.cd.detectChanges();

    this.isDisabled = false;
    //this.lastBase64 = res;
    this.base64.emit(res);

    setTimeout(() => {
      this.isLoading = false;
    }, 1000);
    this.loadingService.deactivate();
  }

  private handleError(err: any): void {
    this.progress = 100;
    this.isLoading = false;
    this.cd.detectChanges();

    this.snackbarService.showDanger(err);
    this.loadingService.deactivate();
  }


  startProgressBar() {
    this.progress = 0;
    this.isLoading = true;

    const duration = 3000;
    const intervalTime = 50;
    const steps = duration / intervalTime;
    const increment = (75) / steps;

    this.progressInterval = setInterval(() => {
      if (this.progress < 75) {
        this.progress += increment;
        if (this.progress >= 75) {
          this.progress = 75;
          clearInterval(this.progressInterval);
        }
        this.cd.detectChanges();
      }
    }, intervalTime);
  }

  stopProgressBar() {
    this.isLoading = false;
    clearInterval(this.progressInterval);
    this.progress = 0;
  }

  get lastBase64Message(): string {
    if (this.lastBase64 ) {

      if (!AttachmentUtils.isBase64(this.lastBase64!)) {
        return "Clique no ícone a direita para visualizar o arquivo";
      }

      const splitValue = this.lastBase64.split(":")[1];
      return splitValue ? splitValue.split(";")[0] : this.defaultMessage;
    }
    return this.defaultMessage;
  }
}
